// Change history and undo (milestone 12), merged into the Audit log: the
// standalone Changes page is gone; a change row in the Audit log expands to the
// change-set's diff and an Undo. HistoryLink opens the Audit log filtered to an
// object's changes.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router'

import { api, can } from '../api'
import { changesApi } from '../changesApi'
import { ErrorBox } from '../components'

/** A link to an object's change history (the Audit log, filtered). */
export function HistoryLink({ kind, slug }: { kind: string; slug: string }) {
  return (
    <Link className="btn" to={`/audit?type=change&object_kind=${encodeURIComponent(kind)}&object_slug=${encodeURIComponent(slug)}`}>
      History
    </Link>
  )
}

/** ChangeDetail shows a change-set's items (field diffs) and an Undo button. */
export function ChangeDetail({ id }: { id: number }) {
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const q = useQuery({ queryKey: ['change', id], queryFn: () => changesApi.get(id) })
  const [confirm, setConfirm] = useState(false)
  const undo = useMutation({
    mutationFn: () => changesApi.undo(id),
    onSuccess: () => {
      qc.invalidateQueries()
      setConfirm(false)
    },
  })
  if (q.error) return <ErrorBox error={q.error} />
  if (!q.data) return <p className="muted">Loading…</p>
  const { set, role } = q.data
  const allowed = can(me.data, role)
  return (
    <div className="change-b">
      <div className="muted mono">change-set #{id}{set.undo_of ? ` · undo of #${set.undo_of}` : ''}{set.undone_by ? ` · undone by #${set.undone_by}` : ''}</div>
      {(set.items ?? []).length === 0 && <p className="note">Nothing in this change can be undone (runs, uploads and cache purges are not versioned).</p>}
      {(set.items ?? []).map((it, i) => (
        <div key={i} className="change-item">
          <strong>
            {it.kind} <span className="mono">{it.slug}</span>
          </strong>
          <Diff before={it.before} after={it.after} />
        </div>
      ))}
      {undo.error && <ErrorBox error={undo.error} />}
      {set.undoable && (
        <div className="row">
          {!confirm ? (
            <button className="btn" disabled={!allowed} title={allowed ? '' : `Undoing this needs the ${role} role`} onClick={() => setConfirm(true)}>
              Undo this change
            </button>
          ) : (
            <>
              <span>Put {set.items?.length === 1 ? 'this object' : `these ${set.items?.length} objects`} back the way they were before #{id}?</span>
              <button className="btn btn-primary" disabled={undo.isPending} onClick={() => undo.mutate()}>
                Undo
              </button>
              <button className="btn" onClick={() => setConfirm(false)}>
                Cancel
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

/** Field-level differences between two snapshots. */
export function Diff({ before, after }: { before: unknown; after: unknown }) {
  if (before == null) return <p className="note">Created in this change. Undo archives, disables or removes it.</p>
  if (after == null) return <p className="note">Removed in this change. Undo brings it back.</p>
  const rows: [string, unknown, unknown][] = []
  const walk = (path: string, a: unknown, b: unknown) => {
    if (a && b && typeof a === 'object' && typeof b === 'object' && !Array.isArray(a) && !Array.isArray(b)) {
      const keys = new Set([...Object.keys(a as object), ...Object.keys(b as object)])
      for (const k of keys) walk(path ? `${path}.${k}` : k, (a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k])
      return
    }
    if (JSON.stringify(a) !== JSON.stringify(b)) rows.push([path, a, b])
  }
  walk('', before, after)
  if (rows.length === 0) return <p className="note">No visible difference.</p>
  const show = (v: unknown) => (v === undefined ? '–' : typeof v === 'string' ? v : JSON.stringify(v))
  return (
    <table className="tbl diff">
      <thead>
        <tr>
          <th>Field</th>
          <th>Before</th>
          <th>After</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([p, a, b]) => (
          <tr key={p}>
            <td className="mono">{p}</td>
            <td className="diff-old">{show(a)}</td>
            <td className="diff-new">{show(b)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
